import { Fragment, type ReactNode } from "react";
import { Icon } from "../../components/ui/Icon";
import { Info } from "../../components/ui/Info";
import { Inline } from "../../components/ui/Rich";
import { headOf, itemsOf, paragraphs } from "./blocks";
import { AddLine, InPlace, NewLine, Piece } from "./InPlace";
import { cap } from "./text";

/**
 * How a playbook part is laid out, as the prototype lays it out (its pbShape): who we go after as audience
 * blocks, what we say as a lead line and angle cards, the never list with stop marks, and every other part
 * as points with their first sentence in bold. It reads only the shape of the owner's markdown. Given `edit`,
 * every paragraph, lead-in and list line is a piece the owner changes in its place.
 */

function label(it: string): [string, string] {
  const m = it.match(/^([^:.]{2,34}):\s+([\s\S]+)$/);
  return m ? [m[1].trim(), m[2].trim()] : ["", it];
}
function firstSentence(p: string): [string, string] {
  const m = p.match(/^([\s\S]+?[.!?])\s+(?=[A-Z"“])/);
  return m ? [m[1], p.slice(m[0].length)] : [p, ""];
}

const REACH = /\b(ads?|email|content|search|linkedin|meta|google|youtube|instagram|tiktok)\b/i;

interface RowAt {
  row: [string, string];
  /** The line's place in its paragraph, which grouping may reorder. */
  j: number;
}

function Row({ i, row: { row: [l, x], j }, cls = "" }: { i: number; row: RowAt; cls?: string }) {
  return (
    <Piece at={{ i, j }} className={`pw-row${l ? "" : " nl"}${cls ? ` ${cls}` : ""}`}>
      {l && <span className="pw-l">{l}</span>}
      <p>
        <Inline text={cap(x)} links />
      </p>
    </Piece>
  );
}

function Point({ p, i }: { p: string; i: number }) {
  const its = itemsOf(p);
  const head = headOf(p);
  if (its.length)
    return (
      <>
        {head && (
          <Piece at={{ i, j: "head" }} as="p" className="pw-intro">
            <Inline text={head} links />
          </Piece>
        )}
        <div className="pw-rows">
          {its.map((it, j) => (
            <Fragment key={j}>
              <Row i={i} row={{ row: label(it), j }} />
              <NewLine i={i} j={j + 1} />
            </Fragment>
          ))}
        </div>
        <AddLine i={i} count={its.length} />
      </>
    );
  const [a, b] = firstSentence(p);
  return (
    <Piece at={{ i }} className="pw-pt">
      <b>
        <Inline text={a} links />
      </b>
      {b && (
        <p>
          <Inline text={b} links />
        </p>
      )}
    </Piece>
  );
}

function Who({ text }: { text: string }) {
  return (
    <>
      {paragraphs(text).map((p, k) => {
        const m = p.match(/^([^\n:]{2,40}):\s*\n/);
        const its = itemsOf(p);
        if (!m || !its.length)
          return (
            <Piece at={{ i: k }} as="p" className="wk-lede" key={k}>
              <Inline text={p} links />
            </Piece>
          );
        const rows = its
          .map((it, j): RowAt => {
            const [l, x] = label(it);
            return { row: !l && /^The next step for \S+ is /i.test(x) ? ["Next step", cap(x.replace(/^The next step for \S+ is /i, ""))] : [l, x], j };
          });
        const reach = rows.filter(({ row: [l] }) => l && REACH.test(l));
        const next = rows.filter(({ row: [l] }) => l === "Next step");
        const side = rows.filter(({ row: [l] }) => !l);
        const rest = rows.filter((r) => !reach.includes(r) && !next.includes(r) && !side.includes(r));
        return (
          <div className="pw-aud" key={k}>
            <Piece at={{ i: k, j: "head" }} as="h3" className="pw-ah">
              <Icon name="users" />
              {m[1]}
            </Piece>
            <div className="pw-rows">
              {rest.map((r) => (
                <Row key={`r${r.j}`} i={k} row={r} />
              ))}
              {next.map((r) => (
                <Row key={`n${r.j}`} i={k} row={r} cls="next" />
              ))}
              {side.map((r) => (
                <Row key={`s${r.j}`} i={k} row={r} cls="side" />
              ))}
            </div>
            {reach.length > 0 && (
              <>
                <div className="wk-sub pw-sub">How we reach them</div>
                <div className="pw-rows">
                  {reach.map((r) => (
                    <Row key={r.j} i={k} row={r} />
                  ))}
                </div>
              </>
            )}
            <NewLine i={k} />
            <AddLine i={k} count={its.length} />
          </div>
        );
      })}
    </>
  );
}

function Say({ text }: { text: string }) {
  return (
    <>
      {paragraphs(text).map((p, k): ReactNode => {
        const its = itemsOf(p);
        if (its.length) {
          const [h1, h2] = firstSentence(headOf(p));
          return (
            <Fragment key={k}>
              {h1 && (
                <Piece at={{ i: k, j: "head" }} className="wk-sub pw-sub h-i">
                  {h1.replace(/[.:]$/, "")}
                  {h2 && <Info label="How ad ideas are used">{h2}</Info>}
                </Piece>
              )}
              <div className="pw-cards">
                {its.map((it, j) => {
                  const [l, x] = label(it);
                  const hold = /^on hold\b/i.test(x);
                  return (
                    <Fragment key={j}>
                      <Piece at={{ i: k, j }} className={`pw-card${hold ? " hold" : ""}`}>
                        <div className="pw-ch">
                          <b>{l || "Ad idea"}</b>
                          {hold && <span className="chip you">On hold</span>}
                        </div>
                        <p>
                          <Inline text={cap(hold ? x.replace(/^on hold\.\s*/i, "") : x)} links />
                        </p>
                      </Piece>
                      <NewLine i={k} j={j + 1} />
                    </Fragment>
                  );
                })}
              </div>
              <AddLine i={k} count={its.length} />
            </Fragment>
          );
        }
        const pains = p.match(/^Pains[^:]*:\s*([\s\S]+)$/i);
        if (pains) {
          const chips = pains[1]
            .replace(/\.$/, "")
            .split(/;\s*/)
            .flatMap((c) => (c.split(", ").length >= 3 ? c.split(", ") : [c]));
          return (
            <Fragment key={k}>
              <div className="wk-sub pw-sub">Pains we can name</div>
              <Piece at={{ i: k }} className="pchips pw-pains">
                {chips.map((c, j) => (
                  <span className="chip" key={j}>
                    {cap(c.trim())}
                  </span>
                ))}
              </Piece>
            </Fragment>
          );
        }
        if (k === 0)
          return (
            <Piece at={{ i: k }} className="pw-lead" key={k}>
              <Icon name="target" />
              <p>
                <Inline text={p} links />
              </p>
            </Piece>
          );
        return (
          <div className="pw-more" key={k}>
            <Point p={p} i={k} />
          </div>
        );
      })}
    </>
  );
}

function Never({ text }: { text: string }) {
  const ps = paragraphs(text);
  const lists = ps.map((p, k) => ({ k, its: itemsOf(p) })).filter((x) => x.its.length);
  if (!lists.length)
    return (
      <>
        {ps.map((p, k) => (
          <Point key={k} p={p} i={k} />
        ))}
      </>
    );
  const last = lists[lists.length - 1];
  return (
    <>
      <ul className="pw-never">
        {lists.flatMap(({ k, its }) =>
          its.map((x, j) => (
            <Fragment key={`${k}.${j}`}>
              <Piece at={{ i: k, j }} as="li">
                <Icon name="ban" />
                <span>
                  <Inline text={x} links />
                </span>
              </Piece>
              <NewLine i={k} j={j + 1} as="li" />
            </Fragment>
          )),
        )}
      </ul>
      <AddLine i={last.k} count={last.its.length} />
    </>
  );
}

function Shaped({ id, text }: { id: string; text: string }) {
  if (id === "who" || id.startsWith("who-")) return <Who text={text} />;
  if (id === "say" || id === "what-we-say") return <Say text={text} />;
  if (id === "never" || id.startsWith("never-")) return <Never text={text} />;
  return (
    <>
      {paragraphs(text).map((p, k) => (
        <Point key={k} p={p} i={k} />
      ))}
    </>
  );
}

/**
 * One playbook part, shaped by what it is about. `id` is the section's slug. With `edit`, the owner changes
 * any piece where it is; `original` is the words before their changes, so the pieces they wrote are marked.
 */
export function PlaybookPart({ id, text, edit }: { id: string; text: string; edit?: { original: string; onChange: (text: string) => void } }) {
  if (!edit) return <Shaped id={id} text={text} />;
  return (
    <InPlace text={text} original={edit.original} onChange={edit.onChange}>
      <Shaped id={id} text={text} />
    </InPlace>
  );
}
